'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';

import {
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  type Locale,
} from '@/i18n/locales';
import { hasLocalizedRoute } from '@/i18n/localized-routes';

/**
 * Language switcher.
 *
 * Renders real <a> links, not a JS-only control, so the localised UI is reachable
 * without JavaScript and the locales are not orphaned behind a script (bible
 * Ch. 2.1). It deliberately does NOT auto-redirect on Accept-Language: Google
 * crawls predominantly from US IPs with `en`, and a language redirect either
 * hides the localised pages from the crawler or bounces it off the English ones
 * (I18N_PLAN.md §4). The user chooses; nothing chooses for them.
 *
 * English lives at the unprefixed path and every other locale at `/<code>/...`,
 * so switching is a path rewrite of the first segment, BUT only for paths that
 * actually have a localised route (LOCALIZED_ROUTE_PATHS). On English-only pages
 * (blog, alternatives, tools, use-case detail, podcast, speech-to-text...) the
 * old switcher emitted `/de/blog/x`, `/ja/alternatives/y` for all 11 locales:
 * ~2,700 crawlable 404s per pass. In the 2026-08-01..15 nginx log, 46% of ALL
 * AI-crawler requests (GPTBot, ClaudeBot, OAI-SearchBot, Perplexity, Meta,
 * Amazon, Apple) were those phantom URLs. Those links now point at the locale
 * root instead, so every switcher href resolves 200 and the visitor still lands
 * in their language.
 */
export function LanguageSwitcher() {
  const pathname = usePathname() || '/';
  const active = useLocale();

  const segments = pathname.split('/').filter(Boolean);
  const hasLocalePrefix = segments.length > 0 && isSupportedLocale(segments[0]);
  // The path minus any locale prefix — the part that stays put when switching.
  const basePath = (hasLocalePrefix ? segments.slice(1) : segments).join('/');

  const localizable = hasLocalizedRoute(basePath ? `/${basePath}` : '/');

  const hrefFor = (locale: Locale) => {
    if (locale === DEFAULT_LOCALE) return basePath ? `/${basePath}` : '/';

    if (!localizable) return `/${locale}`;

    return basePath ? `/${locale}/${basePath}` : `/${locale}`;
  };

  return (
    <nav aria-label="Language" className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {SUPPORTED_LOCALES.map((locale) => {
        const isActive = locale === active;

        return (
          <Link
            key={locale}
            href={hrefFor(locale)}
            hrefLang={locale}
            prefetch={false}
            aria-current={isActive ? 'true' : undefined}
            className={
              isActive
                ? 'text-sm text-[rgb(var(--foreground))] underline underline-offset-4'
                : 'text-sm text-[rgb(var(--foreground-tertiary))] hover:text-[rgb(var(--foreground))] transition-colors'
            }
          >
            {LOCALE_LABELS[locale]}
          </Link>
        );
      })}
    </nav>
  );
}
